import { useCallback, useEffect, useState } from "react";
import { Loader2, AlertTriangle, MapPin, Search, Download } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import { useWorkspace } from "../context/WorkspaceContext";
import { formatInt } from "../lib/format";
import { Badge } from "./ui/badge";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";

const BUCKET_TONE = {
  b0_3: "border-emerald-200 bg-emerald-50 text-emerald-700",
  b4_7: "border-sky-200 bg-sky-50 text-sky-700",
  b8_15: "border-amber-200 bg-amber-50 text-amber-700",
  b15p: "border-red-200 bg-red-50 text-red-700",
};

export default function GroundAgeing({ data: propData }) {
  const { ctx, drillDown } = useWorkspace();
  const pid = ctx.project?.id;
  const [fetched, setFetched] = useState(null);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [userModal, setUserModal] = useState(null);
  const [userRows, setUserRows] = useState([]);
  const [uLoading, setULoading] = useState(false);
  const [pdfBusy, setPdfBusy] = useState(false);
  const usingProp = propData !== undefined;
  const data = usingProp ? propData : fetched;

  const openUsers = (r) => {
    setUserModal({ material_type_id: r.material_type_id, type_name: r.type_name });
    setUserRows([]); setULoading(true);
    api.get("/dashboard/ageing-by-user", { params: { material_type_id: r.material_type_id, ...(pid ? { project_id: pid } : {}) } })
      .then((res) => setUserRows(res.data.users || [])).catch(() => setUserRows([])).finally(() => setULoading(false));
  };
  const exportUsersPdf = async () => {
    if (!userModal) return;
    setPdfBusy(true);
    try {
      const r = await api.get("/dashboard/ageing-by-user/pdf", {
        params: { material_type_id: userModal.material_type_id, ...(pid ? { project_id: pid } : {}) },
        responseType: "blob",
      });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `ground_ageing_${(userModal.type_name || "type").replace(/\s+/g, "_")}.pdf`;
      a.click();
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch { toast.error("PDF export failed"); } finally { setPdfBusy(false); }
  };
  const drillUser = (u, bucketLabel) => {
    const filters = { material_type_id: userModal.material_type_id, field_held: true, label: `${userModal.type_name} · ${u.user_name}${bucketLabel ? ` · ${bucketLabel}` : ""}` };
    if (u.user_id) filters.assigned_field_user_id = u.user_id;
    setUserModal(null);
    drillDown("material-register", filters);
  };

  const load = useCallback(() => {
    if (usingProp) return;
    setLoading(true);
    api.get("/dashboard/ground-ageing", { params: pid ? { project_id: pid } : {} })
      .then((r) => setFetched(r.data)).catch(() => setFetched(null)).finally(() => setLoading(false));
  }, [pid, usingProp]);
  useEffect(() => { load(); }, [load]);

  const drillType = (r) => openUsers(r);

  const t = data?.totals || {};
  const buckets = data?.buckets || [{ key: "b0_3", label: "0-3 days" }, { key: "b4_7", label: "4-7 days" }, { key: "b8_15", label: "8-15 days" }, { key: "b15p", label: ">15 days" }];
  const _q = search.trim().toLowerCase();
  const filteredTypes = (data?.by_type || []).filter((r) => !_q || (r.type_name || "").toLowerCase().includes(_q));

  return (
    <div data-testid="ground-ageing">
      <div className="mb-3 mt-8 flex items-center gap-3">
        <h2 className="font-display text-lg font-semibold text-slate-900">Ground Material Ageing</h2>
        <Badge variant="outline" className="border-slate-300 text-xs font-medium text-slate-500" data-testid="ga-date-basis">Date Basis: Assignment Date</Badge>
        <span className="text-xs text-slate-400">field-held materials (assigned / acknowledged) — click a type to see field users</span>
      </div>

      {loading ? (
        <div className="grid place-items-center rounded-xl border border-slate-200 bg-white py-10"><Loader2 className="h-5 w-5 animate-spin text-slate-400" /></div>
      ) : !data ? (
        <div className="rounded-xl border border-dashed border-slate-200 py-10 text-center text-sm text-slate-400">No ground ageing data</div>
      ) : (
        <>
          <div className="grid grid-cols-2 gap-3 lg:grid-cols-5">
            {buckets.map((b) => (
              <div key={b.key} data-testid={`ga-bucket-${b.key}`} className={`rounded-xl border p-4 ${BUCKET_TONE[b.key]}`}>
                <div className="text-xs font-semibold uppercase tracking-wide">{b.label}</div>
                <div className="mt-1 font-display text-2xl font-bold tabular-nums">{formatInt(t[b.key])}</div>
              </div>
            ))}
            <button type="button" data-testid="ga-overdue-kpi" onClick={() => drillDown("material-register", { label: "Overdue in field (>15 days)" })}
              className="rounded-xl border border-red-300 bg-red-600 p-4 text-left text-white transition-all hover:shadow-md">
              <div className="flex items-center justify-between"><span className="text-xs font-semibold uppercase tracking-wide">Overdue in Field</span><AlertTriangle className="h-4 w-4 opacity-80" /></div>
              <div className="mt-1 font-display text-2xl font-bold tabular-nums">{formatInt(data.overdue)}</div>
              <div className="mt-0.5 text-[11px] opacity-90">&gt;15 days since assignment</div>
            </button>
          </div>

          <div className="mt-5 rounded-xl border border-slate-200 bg-white shadow-sm" data-testid="ga-breakdown-box">
            <div className="flex flex-col gap-2 border-b border-slate-100 p-3 sm:flex-row sm:items-center sm:justify-between">
              <h3 className="text-sm font-semibold text-slate-700">Type-wise Ageing <span className="text-xs font-normal text-slate-400">({filteredTypes.length})</span></h3>
              <div className="relative w-full sm:max-w-xs">
                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                <Input data-testid="ga-search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search material type..." className="h-9 pl-9" />
              </div>
            </div>

            <div className="hidden max-h-[420px] overflow-auto md:block" data-testid="ga-table-desktop">
              <table className="w-full text-left text-sm">
                <thead>
                  <tr className="border-b border-slate-200 bg-slate-100 text-xs uppercase tracking-wide text-slate-500">
                    <th className="sticky top-0 bg-slate-100 px-3 py-2.5 font-semibold">Material Type</th>
                    <th className="sticky top-0 bg-slate-100 px-3 py-2.5 text-right font-semibold">0-3</th>
                    <th className="sticky top-0 bg-slate-100 px-3 py-2.5 text-right font-semibold">4-7</th>
                    <th className="sticky top-0 bg-slate-100 px-3 py-2.5 text-right font-semibold">8-15</th>
                    <th className="sticky top-0 bg-slate-100 px-3 py-2.5 text-right font-semibold">&gt;15</th>
                    <th className="sticky top-0 bg-slate-100 px-3 py-2.5 text-right font-semibold">Total</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredTypes.length === 0 ? (
                    <tr><td colSpan={6} className="px-3 py-8 text-center text-slate-400">{_q ? "No match" : "No field-held materials"}</td></tr>
                  ) : filteredTypes.map((r) => (
                    <tr key={r.material_type_id} data-testid={`ga-row-${r.material_type_id}`} className="cursor-pointer border-b border-slate-100 hover:bg-slate-50" onClick={() => drillType(r)}>
                      <td className="px-3 py-2.5 font-medium text-slate-800"><MapPin className="mr-1 inline h-3.5 w-3.5 text-slate-400" />{r.type_name}</td>
                      <td className="px-3 py-2.5 text-right tabular-nums text-emerald-700">{formatInt(r.b0_3)}</td>
                      <td className="px-3 py-2.5 text-right tabular-nums text-sky-700">{formatInt(r.b4_7)}</td>
                      <td className="px-3 py-2.5 text-right tabular-nums text-amber-700">{formatInt(r.b8_15)}</td>
                      <td className="px-3 py-2.5 text-right tabular-nums">{r.b15p > 0 ? <span className="font-semibold text-red-600">{formatInt(r.b15p)}</span> : "0"}</td>
                      <td className="px-3 py-2.5 text-right font-semibold tabular-nums">{formatInt(r.total)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="max-h-[420px] space-y-3 overflow-auto p-3 md:hidden" data-testid="ga-cards-mobile">
              {filteredTypes.length === 0 ? (
                <div className="py-6 text-center text-sm text-slate-400">{_q ? "No match" : "No field-held materials"}</div>
              ) : filteredTypes.map((r) => (
                <div key={r.material_type_id} className="rounded-lg border border-slate-200 bg-white p-3 shadow-sm" onClick={() => drillType(r)}>
                  <div className="mb-2 font-medium text-slate-800">{r.type_name}</div>
                  <div className="grid grid-cols-5 gap-1 text-center text-xs">
                    <div><div className="text-slate-400">0-3</div><div className="font-semibold text-emerald-700">{formatInt(r.b0_3)}</div></div>
                    <div><div className="text-slate-400">4-7</div><div className="font-semibold text-sky-700">{formatInt(r.b4_7)}</div></div>
                    <div><div className="text-slate-400">8-15</div><div className="font-semibold text-amber-700">{formatInt(r.b8_15)}</div></div>
                    <div><div className="text-slate-400">&gt;15</div><div className="font-semibold text-red-600">{formatInt(r.b15p)}</div></div>
                    <div><div className="text-slate-400">Total</div><div className="font-semibold">{formatInt(r.total)}</div></div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </>
      )}

      <Dialog open={!!userModal} onOpenChange={(o) => !o && setUserModal(null)}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <div className="flex items-center justify-between gap-3 pr-6">
              <DialogTitle className="font-display">{userModal?.type_name} — Field User Ageing</DialogTitle>
              <Button data-testid="ga-user-pdf" size="sm" variant="outline" onClick={exportUsersPdf} disabled={pdfBusy || uLoading || userRows.length === 0}>
                {pdfBusy ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <Download className="mr-1 h-4 w-4" />} Export PDF
              </Button>
            </div>
          </DialogHeader>
          <p className="-mt-1 text-xs text-slate-500">Field-held {userModal?.type_name} by assigned user. Click a bucket to open the Material Register for that user.</p>
          {uLoading ? (
            <div className="grid place-items-center py-10"><Loader2 className="h-5 w-5 animate-spin text-slate-400" /></div>
          ) : userRows.length === 0 ? (
            <div className="py-8 text-center text-sm text-slate-400">No field-held materials for this type</div>
          ) : (
            <div className="max-h-[60vh] overflow-auto rounded-lg border border-slate-200">
              <table className="w-full text-left text-sm" data-testid="ga-user-table">
                <thead><tr className="border-b border-slate-200 bg-slate-100 text-xs uppercase tracking-wide text-slate-500">
                  <th className="sticky top-0 bg-slate-100 px-3 py-2 font-semibold">Field User</th>
                  <th className="sticky top-0 bg-slate-100 px-3 py-2 font-semibold">Code</th>
                  <th className="sticky top-0 bg-slate-100 px-3 py-2 text-right font-semibold">0-3</th>
                  <th className="sticky top-0 bg-slate-100 px-3 py-2 text-right font-semibold">4-7</th>
                  <th className="sticky top-0 bg-slate-100 px-3 py-2 text-right font-semibold">8-15</th>
                  <th className="sticky top-0 bg-slate-100 px-3 py-2 text-right font-semibold">&gt;15</th>
                  <th className="sticky top-0 bg-slate-100 px-3 py-2 text-right font-semibold">Total</th>
                </tr></thead>
                <tbody>
                  {userRows.map((u) => (
                    <tr key={u.user_id || u.user_name} data-testid={`ga-user-row-${u.user_id || "none"}`} className="border-b border-slate-100 hover:bg-slate-50">
                      <td className="px-3 py-2 font-medium text-slate-800">{u.user_name}</td>
                      <td className="px-3 py-2 font-mono text-xs text-slate-500">{u.user_code || "—"}</td>
                      <td className="px-3 py-2 text-right tabular-nums text-emerald-700 cursor-pointer hover:underline" onClick={() => drillUser(u, "0-3 days")}>{formatInt(u.b0_3)}</td>
                      <td className="px-3 py-2 text-right tabular-nums text-sky-700 cursor-pointer hover:underline" onClick={() => drillUser(u, "4-7 days")}>{formatInt(u.b4_7)}</td>
                      <td className="px-3 py-2 text-right tabular-nums text-amber-700 cursor-pointer hover:underline" onClick={() => drillUser(u, "8-15 days")}>{formatInt(u.b8_15)}</td>
                      <td className="px-3 py-2 text-right tabular-nums cursor-pointer hover:underline" onClick={() => drillUser(u, ">15 days")}>{u.b15p > 0 ? <span className="font-semibold text-red-600">{formatInt(u.b15p)}</span> : "0"}</td>
                      <td className="px-3 py-2 text-right font-semibold tabular-nums cursor-pointer hover:underline" onClick={() => drillUser(u)}>{formatInt(u.total)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
